import { useEffect, useState } from "react";
import { Board } from "./black_hole_game";
import { getBookMoveScores, getPositionScore, getSolver6BookDepth } from "./solverService";
import type { BookDepth } from "./solverService";

export type HintResult = "W" | "D" | "L";

export interface Hint {
    /** Win / draw / loss for the side to move under perfect play. */
    result: HintResult;
    /** Perfect-play final margin from the mover's POV (positive = good for you). */
    value: number;
}

export interface HintState {
    /** W/D/L hints by cell key; partial while a live search is still filling in. */
    hints: Map<string, Hint>;
    /** True while a slow live (minmax) search is in progress for this board. */
    pending: boolean;
}

// Hints need the perfect-play score of every legal move. There are two ways to
// get them, and we prefer whichever applies:
//
//   1. The opening book: one cheap lookup that returns every move's score at
//      once. This works for any position the book covers, so hints can turn on
//      early in the game — bounded only by how deep the book reaches.
//   2. A live solve, one cell at a time: always correct but slow. We gate it on
//      the remaining empty count (below) and fill the overlay in incrementally
//      so the user sees progress (cells resolve one by one) rather than a long
//      blank wait.
//
// We try the book first when it covers the position, and fall back to the live
// solver inside its empty-cell gate otherwise.
export const HINT_MAX_EMPTY = 3;

// During a live search we resolve cells one at a time, but pushing every cell to
// React would be a lot of renders when solving is fast. Instead we throttle: the
// overlay is flushed at most once per this interval (plus a final flush at the
// end). A search that finishes within one interval renders exactly once — so
// fast searches never flash placeholders — while slow searches still show
// steady progress.
const HINT_REVEAL_THROTTLE_MS = 100;

/** Cell key matching BoardView's `${row}-${col}` convention. */
function cellKey(row: number, col: number): string {
    return `${row}-${col}`;
}

/** Maps a perfect-play P1-positive score to a hint from the mover's POV. */
function toHint(score: number, perspective: number): Hint {
    const value = score * perspective;
    return { result: value > 0 ? "W" : value < 0 ? "L" : "D", value };
}

/** Empty cells of `board`, as [row, col] pairs, in render order. */
function emptyCells(board: Board): [number, number][] {
    const cells: [number, number][] = [];
    for (let row = 0; row < board.rows; row++) {
        for (let col = 0; col <= row; col++) {
            if (board.at(row, col) === 0) {
                cells.push([row, col]);
            }
        }
    }
    return cells;
}

// Stable empty result so renders without hints don't churn referential identity.
const EMPTY_HINTS: Map<string, Hint> = new Map();
const IDLE: HintState = { hints: EMPTY_HINTS, pending: false };
const WAITING: HintState = { hints: EMPTY_HINTS, pending: true };

/**
 * Computes per-cell W/D/L hints for the side to move, using the opening book
 * where it covers the position and the perfect solver otherwise. Book results
 * arrive all at once; live solves fill in cell by cell, with `pending` true
 * until the last cell resolves. Returns WAITING as soon as a source applies,
 * before its first result arrives, and IDLE while disabled or unsupported.
 */
export function useSolverHints(board: Board, enabled: boolean): HintState {
    // Results are tagged with the board they were computed for, so stale hints
    // from a previous position are never shown.
    const [result, setResult] = useState<{ board: Board; state: HintState } | null>(null);

    // Ply range the 6-row opening book covers; loaded once, used to decide
    // whether book-backed hints apply. Null until loaded / when unavailable.
    const [bookDepth, setBookDepth] = useState<BookDepth | null>(null);
    useEffect(() => {
        if (board.rows !== 6) {
            return;
        }
        let cancelled = false;
        getSolver6BookDepth().then((depth) => {
            if (!cancelled) {
                setBookDepth(depth);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [board.rows]);

    // Start live hints at ply 5 (16 empties) on the standard board, so book
    // misses at its frontier and every later position can still be evaluated.
    const maxEmpty = board.rows === 6 ? 16 : HINT_MAX_EMPTY;
    const liveOk = board.cellCount - board.filled <= maxEmpty;
    // Each book entry holds the per-move scores for its own position, so the
    // book covers this position's hints when its ply is within the book's range.
    const bookOk = bookDepth !== null &&
        board.filled >= bookDepth.minPly &&
        board.filled <= bookDepth.maxPly;

    const active = enabled && !board.isGameOver() && (liveOk || bookOk);

    useEffect(() => {
        if (!active) {
            return;
        }

        let cancelled = false;

        // Positive scores favor player 1; flip to the perspective of the side
        // to move so that a positive outcome always means "good for me".
        const perspective = board.nextMove() > 0 ? 1 : -1;

        const run = async () => {
            // Book first: instant, returns every move's score in one lookup.
            if (bookOk) {
                const fromBook = await getBookMoveScores(board);
                if (cancelled) return;
                if (fromBook) {
                    const hints = new Map<string, Hint>();
                    for (const { row, col, score } of fromBook) {
                        hints.set(cellKey(row, col), toHint(score, perspective));
                    }
                    setResult({ board, state: { hints, pending: false } });
                    return;
                }
            }
            if (!liveOk) {
                setResult({ board, state: IDLE });
                return;
            }

            // Live solve, one cell at a time, revealing each as it resolves.
            // `pending` stays true (showing placeholders on unresolved cells)
            // until the final cell is scored.
            console.info("Hint: minmax search started", {
                filled: board.filled,
                empty: board.cellCount - board.filled,
                nextMove: board.nextMove(),
                bookOk,
            });
            const startMs = performance.now();
            // Accumulate into `working`; snapshot to a fresh Map only on flush so
            // React re-renders at most once per throttle interval.
            const working = new Map<string, Hint>();
            let lastFlushMs = startMs;
            const flush = (pending: boolean) => {
                lastFlushMs = performance.now();
                setResult({ board, state: { hints: new Map(working), pending } });
            };

            for (const [row, col] of emptyCells(board)) {
                const score = await getPositionScore(board.play(row, col));
                if (cancelled) {
                    console.info("Hint: minmax search cancelled", {
                        filled: board.filled,
                        elapsedMs: Math.round(performance.now() - startMs),
                    });
                    return;
                }
                if (score !== null) {
                    working.set(cellKey(row, col), toHint(score, perspective));
                }
                if (performance.now() - lastFlushMs >= HINT_REVEAL_THROTTLE_MS) {
                    flush(true); // mid-search progress; placeholders on the rest
                }
            }

            console.info("Hint: minmax search finished", {
                filled: board.filled,
                elapsedMs: Math.round(performance.now() - startMs),
            });
            flush(false); // final, complete
        };

        run().catch((err) => {
            if (!cancelled) {
                console.error("Failed to compute solver hints:", err);
                setResult({ board, state: IDLE });
            }
        });

        return () => {
            cancelled = true;
        };
    }, [board, active, bookOk, liveOk]);

    if (!active) {
        return IDLE;
    }
    return result?.board === board ? result.state : WAITING;
}
